import PouchDB from 'pouchdb-browser';
import { CookerDatabase } from './base';
/**
 * API functions to get cooker data using promises
 * Uses PouchDB to store cooker data
 * and images as attachments
 * This is the fallback when service workers are not supported.
 */

export class PouchDBCookerDatabase extends CookerDatabase {

    constructor() {
        super();
        this.db = new PouchDB('cookers', { size: 25 });
        this.hoodDB = new PouchDB('hoods', { size: 25 });
        this._image_cache = {};

        let hoodIndex = {
            _id: '_design/hoods',
            views: {
                by_cooker: {
                    map: function (doc) { doc.data.cookers.map( c => emit(c)); }.toString() // eslint-disable-line
                }
            }
        };
        this.hoodDB.put(hoodIndex).catch(() => {});
    }
    clearImageURLCache() {
        Object.keys(this._image_cache).forEach(k => URL.revokeObjectURL(this._image_cache[k]));
        this._image_cache = {};
    }

    /**
     * Search database for cookers matching the spec.
     * TODO: Make sure to clear blob URLs if needed.
     * @return {Promise<Object[]>}
     */
    query(search) {
        return this.db.allDocs({ include_docs: true })
            .then(e => e.rows.map(d => d.doc.data))
            .then(cookers => Promise.all(cookers.filter(cooker => (
                (!search.size || cooker.attributes.size == search.size)
                && (!search.fuel || cooker.attributes.fuel == search.fuel)
                && (
                    (!search.style) ||
                    (search.style == 'traditional' && cooker.attributes.isTraditional) ||
                    (search.style == 'modern' && cooker.attributes.isContemporary)
                )
                && (!search.function || cooker.attributes.isMultifunctionalOven == (search.function == 'multifunction'))
            )).map(async (cooker) => {
                //Cycle each image and find it's object URL or gen one.
                for (let idx in cooker.variations) {
                    const url = cooker.variations[idx].img;
                    if (!this._image_cache[url]) {
                        let blobData = await this.db.getAttachment('' + cooker.id, url);
                        this._image_cache[url] = URL.createObjectURL(blobData);
                    }
                    cooker.variations[idx].img = this._image_cache[url];
                }
                return cooker;
            })))
    }

    async getCookerHoodsFor(cookerId) {
        let results = await this.hoodDB.query('hoods/by_cooker', { include_docs: true, key: cookerId });
        return results.rows.map( r => r.doc.data);
    }

    /**
     * Search w/o attachements, get the count
     * @param {*} search 
     */
    async getFuelOptions(search) {
        return this.db.allDocs({ include_docs: true })
            .then(e => e.rows.map(d => d.doc.data))
            .then(cookers => Promise.all(cookers.filter(cooker => (
                (!search.size || cooker.attributes.size == search.size)
                && (!search.fuel || cooker.attributes.fuel == search.fuel)
                && (
                    (!search.style) ||
                    (search.style == 'traditional' && cooker.attributes.isTraditional) ||
                    (search.style == 'modern' && cooker.attributes.isContemporary)
                )
                && (!search.function || cooker.attributes.isMultifunctionalOven == (search.function == 'multifunction'))
            ))))
            .then(cookers => {
                return cookers.reduce((res, cooker) => {
                    if (res.indexOf(cooker.fuel) === -1) {
                        res.push(cooker.fuel);
                    }
                    return res;
                }, [])
            })
    }

    get(id) {
        return this.db.get('' + id)
            .then(e => e.data)
            .then(async (cooker) => {
                let colourData = await this.getColours();

                for (let idx in cooker.variations) {
                    const url = cooker.variations[idx].img;
                    if (!this._image_cache[url]) {
                        let blobData = await this.db.getAttachment('' + cooker.id, url);
                        this._image_cache[url] = URL.createObjectURL(blobData);
                    }
                    cooker.variations[idx].img = this._image_cache[url];


                    //TODO: Name hacks fix in DB
                    cooker.variations[idx].color = cooker.variations[idx].color == 'SS' ? 'Stainless Steel' : cooker.variations[idx].color;
                    cooker.variations[idx].colorCode = colourData[cooker.variations[idx].color];
                    //TODO: Name hacks fix in DB
                    cooker.variations[idx].colorCode = cooker.variations[idx].color == 'Gloss Black' ? '000000' : cooker.variations[idx].colorCode;
                }
                return cooker;
            })
    }

    /**
     * @callback UpdateCallback
     * @param {number} current
     * @param {number} total
     * @param {string} currentItemName
     */

    /**
     * @param {UpdateCallback} progressCallback
     * Prep database for offline access
     */
    async update(progressCallback = () => { }) {
        progressCallback(0, 1, 'Fetching latest cooker list');
        let newCookerData = await fetch('api/v1/cookers').then(r => r.json())
        let newHoodData = await fetch('/api/v1/cookerhoods').then(r => r.json());
        console.log('data fetched');


        for (let i = 0; i < newHoodData.data.length; i += 3) {
            const hoods = newHoodData.data.slice(i, i + 3);
            const newImages = hoods.map(hood => hood.colours.map(col => `/cache/hoods/${hood.id}/${col}.jpg`)).reduce((a, v) => a.concat(v), [])
            progressCallback(i, newHoodData.data.length, newImages);
            await Promise.all(
                hoods.map(async hood => {

                    let _attachments = {};

                    for (let img of hood.colours.map(col => `/cache/hoods/${hood.id}/${col}.jpg`)) {
                        let pictureData = await fetch(img).then(r => r.blob());
                        _attachments[img] = { content_type: 'image/jpg', data: pictureData };
                    }

                    let _rev = undefined;
                    try {
                        let oldDocument = await this.hoodDB.get('' + hood.id);
                        _rev = oldDocument._rev;
                    } catch (e) {
                        _rev = undefined;
                    }

                    let newDoc = { _id: '' + hood.id, _rev, data: hood, _attachments };
                    console.log('Storing', newDoc);
                    await this.hoodDB.put(newDoc);

                    counter++;
                    progressCallback(counter, newHoodData.data.length, newImages);
                }
                )
            )
        }

        progressCallback(5, 10, 'Purging old hood records');
        const newHoodIds = newHoodData.data.map(c => '' + c.id);

        const hoodsToRemove = (await this.hoodDB.allDocs()).rows.filter(({ id }) => newHoodIds.indexOf(id) === -1 && id.startsWith('_design') === false).map(e => ({ _id: e.id, _rev: e.value.rev }));
        await Promise.all(hoodsToRemove.map(d => this.hoodDB.remove(d)));

        this.clearImageURLCache();
        await this.hoodDB.compact();






        const newMetadata = {
            version: newCookerData.version,
            updated_on: newCookerData.updated_on,
            colours: newCookerData.colours
        }
        // this.db.a
        //Iterate each image, download and get blob
        progressCallback(0, newCookerData.cookers.length, '');

        const cookerData = newCookerData.cookers;
        let counter = 0;

        for (let i = 0; i < cookerData.length; i += 3) {
            const cookers = cookerData.slice(i, i + 3);
            const newImages = cookers.map(cooker => cooker.variations.map(variation => variation.img)).reduce((a, v) => a.concat(v), []);
            progressCallback(counter, newCookerData.cookers.length, newImages);
            await Promise.all(
                cookers.map(async c => {

                    let _attachments = {};

                    for (let img of c.variations.map(variation => variation.img)) {
                        let pictureData = await fetch(img).then(r => r.blob());
                        _attachments[img] = { content_type: 'image/jpg', data: pictureData };
                    }

                    let _rev = undefined;
                    try {
                        let oldDocument = await this.db.get('' + c.id);
                        _rev = oldDocument._rev;
                    } catch (e) {
                        _rev = undefined;
                    }

                    let newDoc = { _id: '' + c.id, _rev, data: c, _attachments };
                    await this.db.put(newDoc);

                    counter++;
                    progressCallback(counter, newCookerData.cookers.length, newImages);
                }
                )
            )
        }
        progressCallback(1, 1, 'Storing metadata');
        try {
            let oldMeta = await this.db.get('_local/meta');
            await this.db.put({ _id: '_local/meta', _rev: oldMeta._rev, ...newMetadata });
        } catch (e) {
            await this.db.put({ _id: '_local/meta', ...newMetadata });
        }

        progressCallback(1, 1, 'Purging old records');
        const newIds = cookerData.map(c => '' + c.id);

        const docsToRemove = (await this.db.allDocs()).rows.filter(({ id }) => newIds.indexOf(id) === -1).map(e => ({ _id: e.id, _rev: e.value.rev }));
        await Promise.all(docsToRemove.map(d => this.db.remove(d)));
        progressCallback(1, 1, 'Cleaning up');

        this.clearImageURLCache();
        await this.db.compact();
        progressCallback(1, 1, 'Done');

    }

    /**
     * Get the release date of the last update
     */
    lastUpdate() {
        return this.db.get('_local/meta').then(doc => doc.updated_on)
    }

    getColours() {
        return this.db.get('_local/meta').then(doc => doc.colours)
    }

    /**
     * Check if an update is available
     */
    updateAvailable() {
        throw new Error('not implemented');
    }

    isFirstRun() {
        return this.db.get('_local/meta').then(() => false, () => true)
    }

    getName() {
        return 'PouchDB';
    }
}


// WEBPACK FOOTER //
// ./src/api/backend/pouchdb.js